import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Filters, Response } from '@ui/src';
import {
  CardsTransactions,
  CardTransaction,
  CustomerType,
  StatementListItemProps,
} from '../../types';
import { customersComponentApi } from '../services/customers';

export interface CustomerAccountsTransactionsState {
  cardsTransactions: {
    isLoading: boolean;
    values?: CardsTransactions[];
    cardId?: string;
    sort?: {
      sortProp: string;
      sortDir: string;
    };
    filters?: Filters;
    requestInProgress: boolean;
  };
  cardTransaction: {
    isLoading: boolean;
    transaction?: CardTransaction;
    transactionId?: string;
  };
  customerType?: CustomerType;
  accountsStatements?: StatementListItemProps[];
  selectedStatements?: string[];
  accountId?: string;
  page?: 'accounts' | 'aml';
}

const initialState: CustomerAccountsTransactionsState = {
  cardsTransactions: {
    isLoading: true,
    requestInProgress: false,
    values: undefined,
  },
  cardTransaction: {
    isLoading: true,
  },
};

export const customerAccountsTransactionsSlice = createSlice({
  name: 'customerAccountsTransactions',
  initialState,
  reducers: {
    selectStatements: (state, action: PayloadAction<string[]>) => {
      state.selectedStatements = action.payload;
    },
    setAccountId: (state, action: PayloadAction<string>) => {
      state.accountId = action.payload;
    },
    getCardsTransactions: (
      state,
      action: PayloadAction<{
        name?: string;
        direction?: 'asc' | 'desc';
        filters?: Filters;
        cardId: string;
        customerType: CustomerType;
        page: 'accounts' | 'aml';
      }>,
    ) => {
      state.cardsTransactions.cardId = action.payload.cardId;
      state.cardsTransactions.requestInProgress = true;
      state.customerType = action.payload.customerType;
      state.page = action.payload.page;

      if (action.payload.name && action.payload.direction) {
        state.cardsTransactions.sort = {
          sortDir: action.payload.direction,
          sortProp: action.payload.name,
        };
      }

      if (action.payload.filters) {
        state.cardsTransactions.filters = action.payload.filters;
      }
    },
    getCardsTransactionsSuccess: (
      state,
      action: PayloadAction<CardsTransactions[]>,
    ) => {
      state.cardsTransactions.isLoading = false;
      state.cardsTransactions.values = action.payload;
      state.cardsTransactions.requestInProgress = false;
    },
    onSortCardsTransactionsList: (
      state,
      action: PayloadAction<{ name: string; direction: 'asc' | 'desc' }>,
    ) => {
      state.cardsTransactions.sort = {
        sortDir: action.payload.direction,
        sortProp: action.payload.name,
      };
      state.cardsTransactions.requestInProgress = true;
    },
    onSortCardsTransactionsListSuccess: (
      state,
      action: PayloadAction<CardsTransactions[]>,
    ) => {
      state.cardsTransactions.requestInProgress = false;
      state.cardsTransactions.values = action.payload;
    },
    onFilterCardsTransactionsList: (state, action: PayloadAction<Filters>) => {
      state.cardsTransactions.requestInProgress = true;
      state.cardsTransactions.filters = action.payload;
    },
    onFilterListCardsTransactionsSuccess: (
      state,
      action: PayloadAction<CardsTransactions[]>,
    ) => {
      state.cardsTransactions.requestInProgress = false;
      state.cardsTransactions.values = action.payload;
    },
    getCardTransaction: (state, action: PayloadAction<string>) => {
      state.cardTransaction.isLoading = true;
      state.cardTransaction.transactionId = action.payload;
    },
    getCardTransactionSuccess: (
      state,
      action: PayloadAction<CardTransaction>,
    ) => {
      state.cardTransaction.isLoading = false;
      state.cardTransaction.transaction = action.payload;
    },
    clearCardTransactionState: (state) => {
      state.cardTransaction.transaction = undefined;
      state.cardTransaction.transactionId = undefined;
    },
    clearAccountsStatementState: (state) => {
      state.accountId = undefined;
    },
    clearCardsStamenetState: (state) => {
      state.cardsTransactions.values = undefined;
      state.cardsTransactions.cardId = undefined;
      state.cardsTransactions.isLoading = true;
    },
    clearAccountsState: (state) => {
      state.accountId = undefined;
      state.cardsTransactions.values = undefined;
      state.cardsTransactions.cardId = undefined;
      state.cardsTransactions.isLoading = true;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.getOverviewStatementsList.matchFulfilled,
      (state, action: PayloadAction<Response<StatementListItemProps>>) => {
        const nextStatements = action.payload.data.edges.map((item) => ({
          ...item.node,
          counterpartyAccountName: `${item.node.counterpartyFirstName || ''} ${
            item.node.counterpartyLastName || ''
          }`,
        }));

        state.accountsStatements = nextStatements;
      },
    );
  },
});

export const {
  selectStatements,
  getCardsTransactions,
  getCardsTransactionsSuccess,
  onFilterCardsTransactionsList,
  onFilterListCardsTransactionsSuccess,
  onSortCardsTransactionsList,
  onSortCardsTransactionsListSuccess,
  clearAccountsStatementState,
  clearCardsStamenetState,
  clearAccountsState,
  getCardTransaction,
  getCardTransactionSuccess,
  clearCardTransactionState,
  setAccountId,
} = customerAccountsTransactionsSlice.actions;

export default customerAccountsTransactionsSlice.reducer;
